import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PaginationState } from "@tanstack/react-table";
import { useAppSelector } from "../../shared/hook";
import { SelectCustom } from "../../shared/Ui/SelectCustom/SelectCustom";
import CheckBox from "../../shared/Ui/CheckBox/CheckBox";
import TableLoader from "../../shared/Ui/Loader/TableLoader";
import AlertBox from "../../shared/Ui/AlertBox/AlertBox";
import ModalComponent from "../../shared/Ui/ModalComponent/ModalWindow";
import ModalButton from "../../shared/Ui/Buttons/ModalButtons";
import { EmptyListPlaceholderContent } from "../../shared/Ui/EmptyPageBlock";
import Pagination from "../../shared/components/Table/Pagination";
import { cleanAllCaches, deleteCachesByKeys, getAllCaches } from "../../common/api/cacheApi";
import { getConsumerList } from "../../common/api/checkApi";
import {
  buildConsumerMap,
  formatCacheValueCell,
  normalizeCacheResponse,
  type CacheType,
  type ICacheRow,
} from "./cacheNormalize";
import style from "./CacheClear.module.css";

export type { CacheType, ICacheRow };

type CacheFilterType = CacheType | "ALL";

const CacheClear: React.FC = () => {
  const { t } = useTranslation();
  const pageSizeDefault = useAppSelector((state) => state.userSettings.pageSize) || 30;

  const cacheTypeOptions = useMemo(
    () => [
      { value: "ALL", label: t("cache.type_all") },
      { value: "TNVED", label: t("cache.type_tnved") },
      { value: "GTIN", label: "GTIN" },
    ],
    [t],
  );

  const [cacheType, setCacheType] = useState<CacheFilterType>("ALL");
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [allRows, setAllRows] = useState<ICacheRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [alertOpen, setAlertOpen] = useState<any>(null);
  const [confirmFullClear, setConfirmFullClear] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSizeDefault || 10,
  });

  const filteredRows = useMemo(() => {
    return allRows.filter((row) => {
      if (cacheType !== "ALL" && row.type !== cacheType) return false;
      if (!appliedSearch.trim()) return true;
      const q = appliedSearch.trim().toLowerCase();
      return row.searchText.includes(q) || row.key.toLowerCase().includes(q);
    });
  }, [allRows, cacheType, appliedSearch]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pagination.pageSize));
  const pageRows = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    return filteredRows.slice(start, start + pagination.pageSize);
  }, [filteredRows, pagination]);

  const cacheTypeLabel = cacheTypeOptions.find((o) => o.value === cacheType)?.label ?? "";

  const loadCaches = useCallback(async () => {
    setIsLoading(true);

    try {
      const [cacheData, consumerData] = await Promise.all([
        getAllCaches(),
        getConsumerList(1, 1000),
      ]);
      const consumerMap = buildConsumerMap(consumerData);
      setAllRows(normalizeCacheResponse(cacheData, consumerMap));
    } catch (error: any) {
      setAllRows([]);
      setAlertOpen({
        status: "error",
        message: error?.response?.data?.message || t("cache.fetch_error"),
      });
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadCaches();
  }, [loadCaches]);

  const handleSearch = () => {
    setAppliedSearch(searchValue);
    setSelectedKeys(new Set());
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  const refreshAfterAction = async () => {
    setSelectedKeys(new Set());
    await loadCaches();
  };

  const handleClearFilter = () => {
    setCacheType("ALL");
    setSearchValue("");
    setAppliedSearch("");
    setSelectedKeys(new Set());
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  const handleCacheTypeChange = (type: CacheFilterType) => {
    setCacheType(type);
    setSelectedKeys(new Set());
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  const toggleRow = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    const pageKeys = pageRows.map((r) => r.key);
    const allSelected = pageKeys.every((k) => selectedKeys.has(k));
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (allSelected) pageKeys.forEach((k) => next.delete(k));
      else pageKeys.forEach((k) => next.add(k));
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedKeys.size === 0) return;
    setIsActionLoading(true);
    try {
      await deleteCachesByKeys(Array.from(selectedKeys));
      setAlertOpen({ status: "SUCCESS", message: t("cache.delete_selected_success") });
      await refreshAfterAction();
    } catch (error: any) {
      setAlertOpen({
        status: "error",
        message: error?.response?.data?.message || t("cache.delete_error"),
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleFullClear = async () => {
    setIsActionLoading(true);
    try {
      if (cacheType === "ALL") {
        await cleanAllCaches();
      } else {
        const keysToDelete = allRows
          .filter((row) => row.type === cacheType)
          .map((row) => row.key);

        if (keysToDelete.length === 0) {
          setAlertOpen({ status: "error", message: t("cache.not_found") });
          return;
        }

        await deleteCachesByKeys(keysToDelete);
      }

      setAlertOpen({ status: "SUCCESS", message: t("cache.full_clear_success") });
      setConfirmFullClear(false);
      await refreshAfterAction();
    } catch (error: any) {
      setAlertOpen({
        status: "error",
        message: error?.response?.data?.message || t("cache.delete_error"),
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <h2 className="my-3 text-xl font-semibold">{t("cache.title")}</h2>

      <div className="bg-white rounded-xl shadow-md p-5 flex flex-col gap-5">
        <div className={style.filterRow}>
          <div className={style.filterField}>
            <label className={style.label}>{t("cache.type")}</label>
            <SelectCustom
              options={cacheTypeOptions}
              value={cacheTypeOptions.find((o) => o.value === cacheType) ?? cacheTypeOptions[0]}
              onChange={(opt) => {
                handleCacheTypeChange((opt?.value as CacheFilterType) ?? "ALL");
              }}
              isSearchable={false}
            />
          </div>

          <div className={style.filterFieldWide}>
            <label className={style.label}>{t("cache.value")}</label>
            <input
              type="text"
              className={style.input}
              value={searchValue}
              placeholder={t("cache.value_placeholder")}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>

          <div className={style.actionsRow}>
            <button
              type="button"
              className="bg-mainColor hover:brightness-75 text-white font-semibold py-2 px-4 h-[40px] rounded whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={handleSearch}
              disabled={isLoading}
            >
              {t("cache.search")}
            </button>
            <button
              type="button"
              className="bg-[#e8edf1] text-mainColor font-semibold py-2 px-4 h-[40px] rounded whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={() => setConfirmFullClear(true)}
              disabled={isActionLoading || isLoading}
            >
              {t("cache.full_clear")}
            </button>
            <button
              type="button"
              className="bg-[#e8edf1] text-mainColor font-semibold py-2 px-4 h-[40px] rounded whitespace-nowrap"
              onClick={handleClearFilter}
            >
              {t("cache.clear_filter")}
            </button>
            {selectedKeys.size > 0 && (
              <button
                type="button"
                className="bg-[#e8edf1] text-mainColor font-semibold py-2 px-4 h-[40px] rounded whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={handleDeleteSelected}
                disabled={isActionLoading}
              >
                {t("cache.clear_selected")}
              </button>
            )}
          </div>
        </div>

        <div className={style.tableWrapper}>
          <table className="w-full leading-normal">
            <thead>
              <tr>
                <th className={style.th} style={{ width: 48 }}>
                  {!isLoading && pageRows.length > 0 && (
                    <CheckBox
                      id="cache-select-all"
                      checked={pageRows.length > 0 && pageRows.every((r) => selectedKeys.has(r.key))}
                      onChange={toggleAllOnPage}
                    />
                  )}
                </th>
                <th className={style.th}>{t("cache.col_number")}</th>
                <th className={style.th}>{t("cache.col_type")}</th>
                <th className={style.th}>{t("cache.col_value")}</th>
                <th className={style.th}>{t("cache.col_consumer")}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="py-10">
                    <TableLoader />
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8">
                    <EmptyListPlaceholderContent title={t("cache.not_found")} />
                  </td>
                </tr>
              ) : (
                pageRows.map((row, index) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className={style.td}>
                      <CheckBox
                        id={`cache-row-${row.id}`}
                        checked={selectedKeys.has(row.key)}
                        onChange={() => toggleRow(row.key)}
                      />
                    </td>
                    <td className={style.td}>
                      {pagination.pageIndex * pagination.pageSize + index + 1}
                    </td>
                    <td className={style.td}>
                      {row.type === "GTIN" ? "GTIN" : t("cache.type_tnved")}
                    </td>
                    <td className={style.td}>{formatCacheValueCell(row)}</td>
                    <td className={style.td}>{row.consumerName}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && filteredRows.length > 0 && (
          <Pagination
            totalItems={filteredRows.length}
            totalPages={totalPages}
            pagination={pagination}
            setPagination={setPagination}
          />
        )}
      </div>

      <ModalComponent
        isOpen={confirmFullClear}
        onClose={() => !isActionLoading && setConfirmFullClear(false)}
        headerContent={
          <h1 className="h__modal">{t("cache.full_clear_confirm_title")}</h1>
        }
        content={
          <div className="flex flex-col gap-4">
            <p className="p__modal">
              {cacheType === "ALL"
                ? t("cache.full_clear_confirm_question_all")
                : t("cache.full_clear_confirm_question", { type: cacheTypeLabel })}
            </p>
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {cacheType === "ALL"
                ? t("cache.full_clear_confirm_warning_all")
                : t("cache.full_clear_confirm_warning")}
            </div>
            <p className="text-sm text-gray-500 leading-relaxed">
              {t("cache.full_clear_confirm_note")}
            </p>
          </div>
        }
        customContent={
          <div className="flex flex-row gap-[10px] justify-end w-full">
            <ModalButton
              type="button"
              color="grey"
              onClick={() => setConfirmFullClear(false)}
              disabled={isActionLoading}
            >
              {t("mainButtons.cancel")}
            </ModalButton>
            <ModalButton type="button" onClick={handleFullClear} disabled={isActionLoading}>
              {isActionLoading ? t("cache.processing") : t("cache.full_clear_confirm_action")}
            </ModalButton>
          </div>
        }
      />

      <AlertBox isOpen={alertOpen} setIsOpen={setAlertOpen} />
    </div>
  );
};

export default CacheClear;
